// ============================================================
// SUPPORT TICKETS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { MessageCircle, Download } from 'lucide-react';
import { Table, Badge, PriorityBadge, Button, SearchInput, Select, Pagination, Modal, StatCard, Tabs } from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { getSupportTickets } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDateTime, downloadCSV } from '../utils/helpers';
import { toast } from 'react-toastify';
import { Headphones } from 'lucide-react';

const SupportTickets = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [tabFilter, setTabFilter] = useState('all');

  useEffect(() => {
    adminApi.getSupportTickets({}, getSupportTickets)
      .then(res => { setAll(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(() => { setAll(getSupportTickets()); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(all, ['id', 'subject', 'customer', 'category']);
  const tabData = tabFilter === 'all' ? filtered : filtered.filter(t => t.status === tabFilter);
  const { currentPage, totalPages, paginated, goTo } = usePagination(tabData);

  const resolveTicket = async (t) => {
    try { await adminApi.resolveTicket(t.id); } catch { /* fallback */ }
    setAll(p => p.map(x => x.id === t.id ? { ...x, status: 'resolved' } : x));
    toast.success('Ticket resolved successfully!');
    setSelected(null);
  };

  const columns = [
    { key: 'id', label: 'Ticket ID', render: v => <span style={{ fontFamily: 'monospace', color: 'var(--admin-primary)', fontWeight: 700 }}>{v}</span> },
    { key: 'subject', label: 'Subject', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'customer', label: 'Customer' },
    { key: 'category', label: 'Category', render: v => <span className="admin-tag">{v}</span> },
    { key: 'priority', label: 'Priority', render: v => <PriorityBadge priority={v} /> },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'createdAt', label: 'Created', render: v => formatDateTime(v) },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="info" size="sm" onClick={() => setSelected(row)}>Reply</Button>
          {row.status === 'open' && <Button variant="success" size="sm" onClick={() => resolveTicket(row)}>Resolve</Button>}
        </div>
      ),
    },
  ];

  const tabs = [
    { key: 'all', label: 'All', count: filtered.length },
    { key: 'open', label: 'Open', count: filtered.filter(t => t.status === 'open').length },
    { key: 'resolved', label: 'Resolved', count: filtered.filter(t => t.status === 'resolved').length },
    { key: 'closed', label: 'Closed', count: filtered.filter(t => t.status === 'closed').length },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Support Tickets & Complaints</h1>
          <p className="admin-page-subtitle">Manage customer support requests</p>
        </div>
        <div className="admin-page-actions">
          <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(all, 'tickets')}>Export</Button>
        </div>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <StatCard label="Total Tickets" value={all.length} icon={<Headphones size={18} />} color="#3B82F6" />
        <StatCard label="Open" value={all.filter(t => t.status === 'open').length} icon={<MessageCircle size={18} />} color="#EF4444" />
        <StatCard label="Resolved" value={all.filter(t => t.status === 'resolved').length} icon={<MessageCircle size={18} />} color="#10B981" />
        <StatCard label="Closed" value={all.filter(t => t.status === 'closed').length} icon={<MessageCircle size={18} />} color="#6B7280" />
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search tickets..." />
        </div>
        <div style={{ padding: '0 20px' }}>
          <Tabs tabs={tabs} active={tabFilter} onChange={v => { setTabFilter(v); goTo(1); }} />
        </div>
        <Table columns={columns} data={paginated} loading={loading} />
        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={tabData.length} itemsPerPage={10} onPageChange={goTo} />
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Ticket: ${selected?.id}`} size="md"
        footer={<>
          <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
          <Button variant="success" onClick={() => { toast.success('Reply sent!'); setSelected(null); setReply(''); }}>Send Reply</Button>
        </>}>
        {selected && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{selected.subject}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Badge status={selected.status} />
                <PriorityBadge priority={selected.priority} />
                <span className="admin-tag">{selected.category}</span>
              </div>
            </div>
            {[
              ['Customer', selected.customer], ['Email', selected.email],
              ['Assigned To', selected.assignedTo], ['Created', formatDateTime(selected.createdAt)],
              ['Updated', formatDateTime(selected.updatedAt)],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-row">
                <span className="admin-info-row-label">{l}</span>
                <span className="admin-info-row-value">{v}</span>
              </div>
            ))}
            <div className="admin-form-group" style={{ marginTop: 16 }}>
              <label>Reply Message</label>
              <textarea className="admin-form-control" rows={4} value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." style={{ resize: 'vertical' }} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SupportTickets;
